import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../../styles/Header.css';
import headerLogo from '../../assets/header_logo.png';
import { useAuth } from '../../contexts/AuthContext';

export default function Header({ activePage = '', onNavigateToSignIn, onHeaderMenuToggle }) {
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const { user, isAuthenticated, signOut: authSignOut } = useAuth();

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        const hamburger = document.querySelector('.hamburger-menu');
        if (hamburger && !hamburger.contains(event.target)) {
          setIsMobileMenuOpen(false);
        }
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden'; // Prevent scrolling when menu is open
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const toggleServicesDropdown = () => {
    setIsServicesDropdownOpen(!isServicesDropdownOpen);
  };

  const closeServicesDropdown = () => {
    setIsServicesDropdownOpen(false);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleLogout = () => {
    authSignOut();
    setIsUserMenuOpen(false);
    navigate('/');
  };

  const handleProfileClick = () => {
    setIsUserMenuOpen(false);
    navigate('/profile');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(prev => {
      const newState = !prev;
      if (onHeaderMenuToggle) {
        onHeaderMenuToggle(newState);
      }
      return newState;
    });
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    if (onHeaderMenuToggle) {
      onHeaderMenuToggle(false);
    }
  };

  return (
    <header className="header">
      <div className="header-container">
        <div className="logo">
		  <Link to="/">
            <img src={headerLogo} alt="Grahboli Logo" className="header-logo-img" />
		  </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="navigation desktop-nav">
          <Link to="/" className={`nav-link ${activePage === 'home' ? 'active' : ''}`}>HOME</Link>
          <div className="nav-dropdown">
            <div
              className={`dropdown-trigger ${activePage === 'services' ? 'active' : ''}`}
              onClick={toggleServicesDropdown}
            >
              <span className={`nav-link ${activePage === 'services' ? 'active' : ''}`}>SERVICES</span>
              <span className="dropdown-arrow">▼</span>
            </div>
            {isServicesDropdownOpen && (
              <div className="dropdown-menu">
                <Link to="/consultation" className="dropdown-item" onClick={closeServicesDropdown}>
                  General consultation
                </Link>
                <Link to="/horoscope" className="dropdown-item" onClick={closeServicesDropdown}>
                  Horoscope reading
                </Link>
              </div>
            )}
          </div>
          <Link to="/astrology" className={`nav-link ${activePage === 'courses' ? 'active' : ''}`}>COURSES</Link>
          <Link to="/about" className={`nav-link ${activePage === 'about' ? 'active' : ''}`}>ABOUT</Link>
          <Link to="/contact" className={`nav-link ${activePage === 'contact' ? 'active' : ''}`}>CONTACT</Link>
        </nav>

        {/* Right Section - Always visible */}
        <div className="header-right-section">
          {/* Auth Section (Login/User Menu) - Hidden on special offer page */}
          {activePage !== 'special-offer' && (
            <>
              {isAuthenticated && user ? (
                <div className="user-menu-container" ref={userMenuRef}>
                  <div className="user-avatar-wrapper" onClick={toggleUserMenu}>
                    <div className="user-avatar">
                      <span className="user-avatar-text">{user.username.charAt(0).toUpperCase()}</span>
                    </div>
                    <svg width="16" height="10" viewBox="0 0 16 10" fill="none" xmlns="http://www.w3.org/2000/svg" className="user-menu-arrow">
                      <path d="M1.33333 1.33333L8 8L14.6667 1.33333" stroke="black" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  {isUserMenuOpen && (
                    <div className="user-dropdown-menu">
                      {user.roles?.includes('ADMIN') && (
                        <button className="user-dropdown-item" onClick={() => { navigate('/admin'); setIsUserMenuOpen(false); }}>
                          Admin Panel
                        </button>
                      )}
                      <button className="user-dropdown-item" onClick={() => { navigate('/my-bookings'); setIsUserMenuOpen(false); }}>
                        My Bookings
                      </button>
                      <button className="user-dropdown-item" onClick={handleProfileClick}>
                        Profile
                      </button>
                      <button className="user-dropdown-item" onClick={handleLogout}>
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button className="login-btn" onClick={onNavigateToSignIn}>LOGIN</button>
              )}
            </>
          )}

          {/* Hamburger Menu Icon - Mobile only */}
          <button className="hamburger-menu" onClick={toggleMobileMenu} aria-label="Toggle menu">
            <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && <div className="mobile-menu-overlay" onClick={closeMobileMenu}></div>}

      {/* Mobile Navigation Menu */}
      <nav ref={mobileMenuRef} className={`mobile-nav ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-nav-header">
          <h3 className="mobile-nav-title">Menu</h3>
          <button className="mobile-nav-close" onClick={closeMobileMenu} aria-label="Close menu">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        <div className="mobile-nav-links">
          <Link to="/" className={`mobile-nav-link ${activePage === 'home' ? 'active' : ''}`} onClick={closeMobileMenu}>
            HOME
          </Link>
          <div className="mobile-nav-section">
            <span className="mobile-nav-section-title">SERVICES</span>
            <Link to="/consultation" className="mobile-nav-sublink" onClick={closeMobileMenu}>
              General consultation
            </Link>
            <Link to="/horoscope" className="mobile-nav-sublink" onClick={closeMobileMenu}>
              Horoscope reading
            </Link>
          </div>
          <Link to="/astrology" className={`mobile-nav-link ${activePage === 'courses' ? 'active' : ''}`} onClick={closeMobileMenu}>
            COURSES
          </Link>
          <Link to="/about" className={`mobile-nav-link ${activePage === 'about' ? 'active' : ''}`} onClick={closeMobileMenu}>
            ABOUT
          </Link>
          <Link to="/contact" className={`mobile-nav-link ${activePage === 'contact' ? 'active' : ''}`} onClick={closeMobileMenu}>
            CONTACT
          </Link>
        </div>
      </nav>
    </header>
  );
}